import { useState, useEffect } from 'react';
import {
  Box, TextField, InputAdornment, Alert, Snackbar,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import useDebounce from '../../../hooks/useDebounce';
import permissionService from '../services/permissionService';
import PermissionTable from '../components/PermissionTable';
import LoadingLogo from '../../../components/LoadingLogo';

const PermissionList = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadPermissions();
  }, [page, debouncedSearch]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const result = await permissionService.getAll({ page, search: debouncedSearch, limit: 50 });
      setPermissions(result.data);
      setPagination(result.pagination);
    } catch (error) {
      console.error('Error loading permissions:', error);
      showSnackbar('Error al cargar permisos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <Box>
      <PageHeader title="Permisos" />

      <Box sx={{ bgcolor: '#ffffff', borderRadius: '14px', p: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Buscar permisos..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LoadingLogo size={120} />
        </Box>
      ) : (
        <PermissionTable
          permissions={permissions}
          page={page}
          onPageChange={handlePageChange}
          pagination={pagination}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionList;
